import { AdminHeaderIconButton } from '@/components/patterns/admin-page'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { Can } from '@/lib/casl'
import { cn } from '@/lib/utils'
import {
  PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED,
  PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT,
  PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
} from '@/lib/services/price-adjustments/priceAdjustments.schema'
import { EllipsisVertical, PencilLine, Tags, Trash2 } from 'lucide-react'
import { useState } from 'react'
import FieldInfo from '../../form/FieldInfo'
import ProductPriceAdjustmentScopePicker from '../ProductPriceAdjustmentScopePicker'
import {
  adjustmentTypeOptions,
  operationOptions,
  roundingOptions,
  showPageCardClass,
  showPageIconShellClass,
  showPageWhenTypeOptions,
  whenTypeOptions,
} from './constants'
import { PriceAdjustmentFieldInfo } from './FieldInfo'
import { PriceAdjustmentScopeChangePreview } from './ScopeChangePreview'
import { PriceAdjustmentTimingModeNotice } from './TimingModeNotice'

export function PriceAdjustmentDetailsSection({ controller }) {
  const {
    clearScheduleSectionError,
    clearScopeSectionError,
    clearWhenSectionError,
    form,
    getRadioButtonGroupProps,
    id,
    rememberCurrentScheduledTimingDraft,
    scopeAndChangeSectionId,
    scrollToScheduleSectionOnNextFrame,
    selectableCollections,
    showPageTreatment,
    switchToActiveUntilDeletedMode,
    switchToPermanentSchedule,
    switchToScheduledMode,
  } = controller

  return (
    <>
      {showPageTreatment ? <PriceAdjustmentNameCard controller={controller} /> : null}

      <Card
        id={scopeAndChangeSectionId}
        accent={showPageTreatment ? 'info' : 'none'}
        className={showPageTreatment ? cn('scroll-mt-24', showPageCardClass) : undefined}
      >
        <CardHeader className={showPageTreatment ? 'px-5 pt-5 pb-3 sm:px-6 sm:pt-6' : ''}>
          <CardTitle className="inline-flex items-center gap-2">
            {showPageTreatment ? (
              <span className={cn(showPageIconShellClass, 'text-chart-5')}>
                <Tags className="size-5" />
              </span>
            ) : (
              <Tags className="h-4 w-4" />
            )}
            <Typography variant="h4">Details</Typography>
          </CardTitle>
        </CardHeader>
        <CardContent className={cn('grid gap-y-6', showPageTreatment && 'px-5 pb-6 sm:px-6')}>
          {!showPageTreatment ? <PriceAdjustmentNameField form={form} id={id} /> : null}

          <form.Subscribe selector={(state) => state.values.scope_type}>
            {(scopeType) => (
              <form.AppField name="collection_ids">
                {(field) => (
                  <div
                    className={cn(
                      'space-y-2',
                      showPageTreatment && 'border-b border-border/20 pb-5 dark:border-white/10'
                    )}
                  >
                    <ProductPriceAdjustmentScopePicker
                      scopeType={scopeType}
                      collectionIds={field.state.value}
                      collections={selectableCollections}
                      showPageTreatment={showPageTreatment}
                      onScopeTypeChange={(nextScopeType) => {
                        clearScopeSectionError()
                        form.setFieldValue('scope_type', nextScopeType)
                      }}
                      onCollectionIdsChange={(nextCollectionIds) => {
                        clearScopeSectionError()
                        field.handleChange(nextCollectionIds)
                      }}
                    />
                    <PriceAdjustmentFieldInfo />
                  </div>
                )}
              </form.AppField>
            )}
          </form.Subscribe>

          <div className={showPageTreatment ? '@container/price-adjustment-change' : undefined}>
            <div
              className={cn(
                'grid gap-5',
                showPageTreatment
                  ? 'grid-cols-[auto_minmax(0,1fr)] items-start @[38rem]/price-adjustment-change:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] @[38rem]/price-adjustment-change:gap-x-8'
                  : 'xl:grid-cols-[auto_auto_minmax(0,1fr)] xl:items-start'
              )}
            >
              <div
                className={
                  showPageTreatment ? 'col-span-2 @[38rem]/price-adjustment-change:col-span-1' : ''
                }
              >
                <form.AppField name="operation">
                  {(field) => (
                    <field.RadioButtonGroup
                      id={`${id}-operation`}
                      label="Direction"
                      options={operationOptions}
                      size="sm"
                      {...getRadioButtonGroupProps({
                        container: showPageTreatment
                          ? 'grid w-full min-w-0 grid-cols-2'
                          : 'w-full sm:w-auto',
                        button: showPageTreatment
                          ? 'min-w-0 items-center justify-center gap-1.5 px-2 leading-none'
                          : 'flex-1 sm:flex-none',
                      })}
                    />
                  )}
                </form.AppField>
              </div>

              <div
                className={
                  showPageTreatment
                    ? 'col-start-1 @[38rem]/price-adjustment-change:col-start-auto'
                    : ''
                }
              >
                <form.AppField name="adjustment_type">
                  {(field) => (
                    <field.RadioButtonGroup
                      id={`${id}-adjustment-type`}
                      label="Amount Type"
                      options={adjustmentTypeOptions}
                      size="sm"
                      {...getRadioButtonGroupProps({
                        container: showPageTreatment ? 'inline-flex' : 'w-full sm:w-auto',
                        button: showPageTreatment
                          ? 'justify-center @[38rem]/price-adjustment-change:min-w-14'
                          : 'flex-1 sm:flex-none min-w-12',
                      })}
                    />
                  )}
                </form.AppField>
              </div>

              <form.Subscribe selector={(state) => state.values.adjustment_type}>
                {(adjustmentType) => (
                  <div
                    className={
                      showPageTreatment
                        ? 'col-start-2 min-w-0 @[38rem]/price-adjustment-change:col-start-auto'
                        : ''
                    }
                  >
                    <form.AppField name="amount">
                      {(field) =>
                        adjustmentType === 'flat' ? (
                          <field.MoneyInput
                            id={`${id}-amount-flat`}
                            label="Amount"
                            className="max-w-sm"
                            inputWrapperClassName={showPageTreatment ? 'translate-y-1' : ''}
                            showFreeBadge={false}
                            formatEmptyAsZero={false}
                          />
                        ) : adjustmentType === 'percent' ? (
                          <field.PercentInput
                            id={`${id}-amount-percent`}
                            label="Percent"
                            min="0.01"
                            max={9999}
                            placeholder="10"
                            className="max-w-sm"
                            inputWrapperClassName={showPageTreatment ? 'translate-y-1' : ''}
                          />
                        ) : (
                          <EmptyState
                            size="compact"
                            surface="transparent"
                            title="Choose an amount type before entering the price change."
                          />
                        )
                      }
                    </form.AppField>
                  </div>
                )}
              </form.Subscribe>
            </div>
          </div>

          <div className={showPageTreatment ? 'max-w-full' : ''}>
            <form.AppField name="rounding_mode">
              {(field) => (
                <field.RadioButtonGroup
                  id={`${id}-rounding-mode`}
                  label="Rounding"
                  className={showPageTreatment ? 'w-fit max-w-full' : ''}
                  options={roundingOptions}
                  size="sm"
                  {...getRadioButtonGroupProps({
                    container: showPageTreatment ? 'w-fit max-w-full' : 'w-full sm:w-auto',
                    button: showPageTreatment
                      ? 'min-w-[8.25rem] justify-center whitespace-nowrap'
                      : 'flex-1 whitespace-nowrap sm:flex-none',
                  })}
                />
              )}
            </form.AppField>
          </div>

          <form.Subscribe
            selector={(state) => ({
              adjustment_type: state.values.adjustment_type,
              amount: state.values.amount,
              operation: state.values.operation,
              rounding_mode: state.values.rounding_mode,
              scope_type: state.values.scope_type,
              collection_ids: state.values.collection_ids,
            })}
          >
            {(value) => (
              <PriceAdjustmentScopeChangePreview
                value={value}
                selectableCollections={selectableCollections}
              />
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.values.when_type}>
            {(whenType) => (
              <div className="space-y-4 border-t border-border/20 pt-5 dark:border-white/10">
                <form.AppField
                  name="when_type"
                  listeners={{
                    onChange: ({ value }) => {
                      clearWhenSectionError()
                      clearScheduleSectionError()

                      if (
                        whenType === PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED &&
                        value !== PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED
                      ) {
                        rememberCurrentScheduledTimingDraft()
                      }

                      if (value === PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED) {
                        switchToActiveUntilDeletedMode()
                      }

                      if (value === PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED) {
                        switchToScheduledMode()
                        scrollToScheduleSectionOnNextFrame()
                      }

                      if (value === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT) {
                        switchToPermanentSchedule()
                      }
                    },
                  }}
                >
                  {(field) => (
                    <field.RadioButtonGroup
                      id={`${id}-when-type`}
                      label="When"
                      className={showPageTreatment ? '@container/price-adjustment-when w-full' : ''}
                      aria-label="When"
                      options={showPageTreatment ? showPageWhenTypeOptions : whenTypeOptions}
                      size="sm"
                      {...(showPageTreatment
                        ? {
                            appearance: 'selectorCard',
                            joined: false,
                            unselectedVariant: 'outline',
                            classNames: {
                              container:
                                'grid w-full gap-3 @[38rem]/price-adjustment-when:grid-cols-3',
                            },
                          }
                        : getRadioButtonGroupProps({
                            container: 'w-full sm:w-auto',
                            button: 'flex-1 sm:flex-none',
                          }))}
                    />
                  )}
                </form.AppField>

                <PriceAdjustmentTimingModeNotice whenType={whenType} />

                {whenType !== PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED ? (
                  <form.AppField name="schedule">
                    {() => <PriceAdjustmentFieldInfo className="block" />}
                  </form.AppField>
                ) : null}
              </div>
            )}
          </form.Subscribe>
        </CardContent>
      </Card>
    </>
  )
}

function PriceAdjustmentNameCard({ controller }) {
  const { form, handleDelete, id, initialData, deleteMutation } = controller

  return (
    <Card accent="info" className={showPageCardClass}>
      <CardHeader className="px-5 pt-5 pb-3 sm:px-6 sm:pt-6">
        <CardTitle className="inline-flex items-center gap-2">
          <span className={cn(showPageIconShellClass, 'text-chart-5')}>
            <PencilLine className="size-5" />
          </span>
          <Typography variant="h4">Name</Typography>
        </CardTitle>
        {initialData?.id ? (
          <CardAction>
            <PriceAdjustmentMoreActionsMenu
              isPending={deleteMutation.isPending}
              onDelete={handleDelete}
            />
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="px-5 pb-6 sm:px-6">
        <PriceAdjustmentNameField form={form} id={id} />
      </CardContent>
    </Card>
  )
}

function PriceAdjustmentMoreActionsMenu({ isPending = false, onDelete }) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const openDeleteDialog = () => {
    setIsDeleteDialogOpen(true)
  }

  return (
    <Can do="edit" on="products">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderIconButton icon={EllipsisVertical} label="More price change actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="md">
          <DropdownMenuItem variant="destructive" disabled={isPending} onSelect={openDeleteDialog}>
            <Trash2 className="size-4" />
            Delete price change
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmationDialog
        trigger={null}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        variant="destructive"
        title="Delete Price Change"
        description="Are you sure you want to delete this price change?"
        confirmText="Delete"
        pendingText="Deleting..."
        isPending={isPending}
        onConfirm={onDelete}
      />
    </Can>
  )
}

function PriceAdjustmentNameField({ form, id }) {
  return (
    <form.AppField name="name">
      {(field) => (
        <div className="space-y-2">
          <field.TextInput
            id={`${id}-name`}
            label="Name this price change so you can easily find it."
            placeholder="Mother's Day premium"
            className="max-w-xl"
          />
          <FieldInfo />
        </div>
      )}
    </form.AppField>
  )
}
